import { useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch } from 'react';
import { captureNetworkError } from '../../sentry/capture-network-error';
import { parseFunnelbackJsonResponse } from '../../utils/parse-funnelback-json-response';
import { buildSafeFunnelbackLookupFetchUrl } from '../../utils/funnelback-fetch-url';
import { fetchWithFunnelbackRetry } from '../../utils/funnelback-fetch';
import type { SearchAppContextType, SearchKeyValueParam } from '../../types';
import {
  HYDRATE_FROM_URL,
  SEARCH_FAILED,
  SEARCH_PENDING,
  SEARCH_SUCCESS,
} from './search-app-actions';
import type { SearchAppAction } from './search-app-reducer';
import { parseFunnelbackSearchResponse } from './parse-funnelback-search-response';
import {
  buildFunnelbackFacetParams,
  funnelbackFacetParamsCacheKey,
} from './funnelback-toggle-url';
import {
  mergeLookupExtrasWithDefaults,
  parseSearchParamsFromSearch,
  replaceHistorySearchParams,
  FUNNELBACK_SHOW_ALL_QUERY,
  SEARCH_SHOW_ALL_QUERY,
  serializeSearchParamsToQueryString,
  subscribePopState,
} from './search-url';

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Debounced Funnelback search requests, URL sync, and browser back/forward.
 */
export function useSearchAppQuery(
  state: SearchAppContextType,
  dispatch: Dispatch<SearchAppAction>,
  options: { debounceMs?: number } = {},
): void {
  const { debounceMs = 350 } = options;
  const debouncedQuery = useDebouncedValue(state.query, debounceMs);

  const lookupExtrasMerged = useMemo(
    () => mergeLookupExtrasWithDefaults(state.lookupExtras),
    [state.lookupExtras],
  );

  const funnelbackFacetsRef = useRef(state.funnelbackFacets);
  funnelbackFacetsRef.current = state.funnelbackFacets;

  const selectionRequestKey = useMemo(
    () => funnelbackFacetParamsCacheKey(state.selectedFacets),
    [state.selectedFacets],
  );

  const lastFetchedUrlRef = useRef<string | null>(null);
  const lastSelectionRequestKeyRef = useRef(selectionRequestKey);

  useEffect(() => {
    return subscribePopState(() => {
      const next = parseSearchParamsFromSearch(window.location.search);
      dispatch({ type: HYDRATE_FROM_URL, payload: next });
    });
  }, [dispatch]);

  useEffect(() => {
    const queryForUrl =
      state.query === '' ? SEARCH_SHOW_ALL_QUERY : state.query;
    const qs = serializeSearchParamsToQueryString({
      query: queryForUrl,
      page: state.page,
      selectedFacets: state.selectedFacets,
      preserveParams: new URLSearchParams(window.location.search),
    });
    const current = window.location.search.replace(/^\?/, '');
    if (qs !== current) {
      replaceHistorySearchParams(qs);
    }
  }, [state.query, state.page, state.selectedFacets]);

  useEffect(() => {
    const lookupBaseUrl = state.lookupBaseUrl.trim();
    if (!lookupBaseUrl) {
      return;
    }

    const pageSize = Math.max(1, state.pageSize);
    const page = Math.max(1, state.page);
    const startRank = (page - 1) * pageSize + 1;
    const paginationParams: SearchKeyValueParam[] = [
      { key: 'num_ranks', value: String(pageSize) },
      { key: 'start_rank', value: String(startRank) },
    ];
    const trimmedQuery = debouncedQuery;

    const facetParams = buildFunnelbackFacetParams({
      selectedFacets: state.selectedFacets,
      funnelbackFacets: funnelbackFacetsRef.current,
    });
    const extras = [
      ...lookupExtrasMerged,
      ...facetParams,
      ...paginationParams,
    ];
    const queryForRequest =
      trimmedQuery === '' ? FUNNELBACK_SHOW_ALL_QUERY : trimmedQuery;
    const url = buildSafeFunnelbackLookupFetchUrl(
      lookupBaseUrl,
      queryForRequest,
      extras,
    );

    if (!url) {
      dispatch({
        type: SEARCH_FAILED,
        payload: { error: 'Search could not be completed.' },
      });
      return;
    }

    if (lastSelectionRequestKeyRef.current !== selectionRequestKey) {
      lastSelectionRequestKeyRef.current = selectionRequestKey;
      lastFetchedUrlRef.current = null;
    }

    if (url === lastFetchedUrlRef.current) {
      return;
    }
    lastFetchedUrlRef.current = url;

    const controller = new AbortController();
    dispatch({ type: SEARCH_PENDING });

    fetchWithFunnelbackRetry(url, { signal: controller.signal })
      .then((response) => parseFunnelbackJsonResponse(response))
      .then((data: unknown) => {
        const parsed = parseFunnelbackSearchResponse(data);
        if (parsed.funnelbackError) {
          captureNetworkError(new Error(parsed.funnelbackError), {
            feature: 'search-app',
            request: 'funnelback-search',
            url,
          });
          dispatch({
            type: SEARCH_FAILED,
            payload: { error: parsed.funnelbackError },
          });
          return;
        }
        dispatch({
          type: SEARCH_SUCCESS,
          payload: {
            results: parsed.results,
            totalResults: parsed.totalResults,
            facetMembers: parsed.facetMembers,
            funnelbackFacets: parsed.funnelbackFacets,
            facetExtras: parsed.facetExtras,
            questionEcho: parsed.questionEcho,
            returnCode: parsed.returnCode,
            page: state.page,
            pageSize: state.pageSize,
          },
        });
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') {
          return;
        }
        captureNetworkError(err, {
          feature: 'search-app',
          request: 'funnelback-search',
          url,
        });
        dispatch({
          type: SEARCH_FAILED,
          payload: { error: 'Search could not be completed.' },
        });
      });

    return () => {
      controller.abort();
    };
  }, [
    debouncedQuery,
    dispatch,
    selectionRequestKey,
    lookupExtrasMerged,
    state.lookupBaseUrl,
    state.page,
    state.pageSize,
  ]);
}
